import React, { useState, useEffect } from 'react';
import { DateService } from '../utils/DateService';
import { useAuth } from '../AuthContext';
import { useStreak } from '../contexts/StreakContext';
import { Settings, Calendar, Zap, RefreshCw } from 'lucide-react';

const ADMIN_EMAILS = [''];

export const AdminStreakControls: React.FC = () => {
    const { currentUser } = useAuth();
    const { trackActivity, resetStreak } = useStreak();
    const [isVisible, setIsVisible] = useState(false);
    const [currentDate, setCurrentDate] = useState<Date>(DateService.now());
    const [simulatedDateStr, setSimulatedDateStr] = useState<string | null>(DateService.getSimulatedDate());

    useEffect(() => {
        if (currentUser && ADMIN_EMAILS.includes(currentUser.email || '')) {
            setIsVisible(true);
            // Refresh date every minute or so? Or just on mount
            setCurrentDate(DateService.now());
            setSimulatedDateStr(DateService.getSimulatedDate());
        }
    }, [currentUser]);

    if (!isVisible) return null;

    const handleDateChange = (days: number) => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + days);
        DateService.setSimulatedDate(newDate);
        // Page reloads automatically in DateService
    };

    const handleResetDate = () => {
        DateService.setSimulatedDate(null);
    };

    const handleInstantActivity = async () => {
        if (!window.confirm('Simulate 5 flashcards reviewed now?')) return;

        try {
            await trackActivity('flashcard_reviewed', 5);
            alert('Activity tracked! Check logs/streak.');
        } catch (e) {
            alert('Error tracking activity');
            console.error(e);
        }
    };

    // Format date manually to avoid external deps
    const formatDate = (date: Date) => {
        return date.getFullYear() + '-' +
            String(date.getMonth() + 1).padStart(2, '0') + '-' +
            String(date.getDate()).padStart(2, '0') + ' ' +
            String(date.getHours()).padStart(2, '0') + ':' +
            String(date.getMinutes()).padStart(2, '0');
    };

    return (
        <div className="fixed bottom-4 right-4 z-[9999]">
            <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-4 w-72 text-white opacity-90 hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                    <h3 className="font-bold flex items-center gap-2 text-yellow-400">
                        <Settings size={16} /> Admin Streak Tools
                    </h3>
                    <span className="text-xs bg-gray-800 px-2 py-0.5 rounded text-gray-400">
                        {simulatedDateStr ? 'Time Travel Active' : 'Real Time'}
                    </span>
                </div>

                {/* Date Controls */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-300 flex items-center gap-1">
                            <Calendar size={14} /> Date:
                        </span>
                        <span className="font-mono text-xs">{formatDate(currentDate)}</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-2">
                        <button
                            onClick={() => handleDateChange(-1)}
                            className="bg-gray-800 hover:bg-gray-700 px-2 py-1 rounded text-xs"
                        >
                            -1 Day
                        </button>
                        <button
                            onClick={handleResetDate}
                            className={`px-2 py-1 rounded text-xs ${!simulatedDateStr ? 'bg-green-900 text-green-300' : 'bg-gray-800 hover:bg-gray-700'}`}
                        >
                            Today
                        </button>
                        <button
                            onClick={() => handleDateChange(1)}
                            className="bg-gray-800 hover:bg-gray-700 px-2 py-1 rounded text-xs"
                        >
                            +1 Day
                        </button>
                    </div>
                </div>

                {/* Activity Controls */}
                <div className="space-y-2">
                    <button
                        onClick={handleInstantActivity}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2"
                    >
                        <Zap size={14} /> Instant Flashcard Streak (5x)
                    </button>
                    <button
                        onClick={async () => {
                            if (window.confirm('Reset streak to 0?')) {
                                await resetStreak();
                                alert('Streak reset locally.');
                            }
                        }}
                        className="w-full bg-red-900/50 hover:bg-red-900 text-red-200 text-xs py-1 px-3 rounded flex items-center justify-center gap-2"
                    >
                        <RefreshCw size={12} /> Reset Streak
                    </button>
                </div>
            </div>
        </div>
    );
};
